import { createRouter, createWebHistory } from "vue-router";
import HomeView from "./components/views/HomeView.vue";
import ContactView from "./components/views/ContactView.vue";
import HowItWorks from "./components/views/HowItWorks.vue";
import AboutMeView from "./components/views/AboutMeView.vue";
import PrivacyView from "./components/views/PrivacyView.vue";
import TermsView from "./components/views/TermsView.vue";
import NotFoundView from "./components/views/NotFoundView.vue";
import SeoLandingView from "./components/views/SeoLandingView.vue";
import { seoPages } from "./data/seoPages";

const coreRoutes = [
  {
    path: "/",
    name: "HomeView",
    component: HomeView,
    meta: {
      title: "Online Accounting Tutor for College Students | AliTutor",
      description:
        "One-on-one online accounting tutoring with patient explanations, relevant practice, and plans from $35 per session.",
    },
  },
  {
    path: "/contact",
    name: "ContactView",
    component: ContactView,
    meta: {
      title: "Book a Free Tutoring Consultation | AliTutor",
      description:
        "Start with a free 30-minute consultation for one-on-one accounting, math, or Excel tutoring with AliTutor.",
    },
  },
  {
    path: "/howitworks",
    name: "HowItWorks",
    component: HowItWorks,
    meta: {
      title: "How One-on-One Tutoring Works | AliTutor",
      description:
        "A clear three-step tutoring process: identify the gap, build the logic, and practice until you can solve it independently.",
    },
  },
  {
    path: "/about",
    name: "AboutMeView",
    component: AboutMeView,
    meta: {
      title: "About Ali Khalil, College Tutor | AliTutor",
      description:
        "Meet Ali Khalil, a former SUNY Ulster peer tutor offering patient, step-by-step accounting and math support for college students.",
    },
  },
  {
    path: "/products",
    redirect: { path: "/", hash: "#pricing" },
  },
  {
    path: "/privacy",
    name: "PrivacyView",
    component: PrivacyView,
    meta: {
      title: "Privacy Policy | AliTutor",
      description:
        "Learn how AliTutor handles information provided through booking requests and email.",
    },
  },
  {
    path: "/terms",
    name: "TermsView",
    component: TermsView,
    meta: {
      title: "Tutoring Terms | AliTutor",
      description:
        "Review the terms for AliTutor consultations and tutoring services.",
    },
  },
];

const seoRoutes = seoPages.map((page) => ({
  path: page.slug,
  name: `seo-${page.id}`,
  component: SeoLandingView,
  props: { pageId: page.id },
  meta: {
    title: page.title,
    description: page.description,
    canonical: `https://alitutor.com${page.slug}`,
  },
}));

const routes = [
  ...coreRoutes,
  ...seoRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFoundView,
    meta: {
      title: "Page Not Found | AliTutor",
      description: "The requested AliTutor page could not be found.",
      noindex: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, top: 90, behavior: "smooth" };
    return { top: 0 };
  },
});

function upsertMeta(selector, attribute, value) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    const match = selector.match(/meta\[(name|property)="([^"]+)"\]/);
    if (match) element.setAttribute(match[1], match[2]);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
}

router.afterEach((to) => {
  const title = to.meta.title || "AliTutor | College Tutoring";
  const description = to.meta.description || "One-on-one tutoring for college students.";
  const canonicalUrl = to.meta.canonical || `https://alitutor.com${to.path === "/" ? "/" : to.path}`;

  document.title = title;
  upsertMeta('meta[name="description"]', "content", description);
  upsertMeta('meta[name="robots"]', "content", to.meta.noindex ? "noindex, nofollow" : "index, follow");
  upsertMeta('meta[property="og:title"]', "content", title);
  upsertMeta('meta[property="og:description"]', "content", description);
  upsertMeta('meta[property="og:url"]', "content", canonicalUrl);
  upsertMeta('meta[property="og:type"]', "content", "website");

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", canonicalUrl);
});

export default router;
